import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

import { Save, Eye, Plus, Undo, Redo, Monitor, Tablet, Smartphone, X, HelpCircle } from 'lucide-react';
import SortableBlock from './SortableBlock';
import BlockEditorModal from './BlockEditorModal';
import PagePreview from './PagePreview';
import { getToken } from '../../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json'
});

const VisualPageEditor = ({ pageId, onClose }) => {
  const [page, setPage] = useState(null);
  const [_blocks, _setBlocks] = useState([]);
  const [availableBlocks, setAvailableBlocks] = useState([]);
  const [pageBlocks, setPageBlocks] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [previewMode, setPreviewMode] = useState(false);
  const [devicePreview, setDevicePreview] = useState('desktop'); // desktop, tablet, mobile
  const [editingBlock, setEditingBlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved'
  const [lastSaved, setLastSaved] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + S pour sauvegarder
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      // Ctrl/Cmd + Z pour Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Ctrl/Cmd + Shift + Z ou Ctrl/Cmd + Y pour Redo
      if ((e.ctrlKey || e.metaKey) && (e.shiftKey && e.key === 'z' || e.key === 'y')) {
        e.preventDefault();
        redo();
      }
      // Ctrl/Cmd + P pour Preview
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        setPreviewMode(!previewMode);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [previewMode, historyIndex, history]);

  useEffect(() => {
    loadPage();
    loadAvailableBlocks();
  }, [pageId]);

  const loadPage = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cms/pages/${pageId}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setPage(data);
      setPageBlocks(data.blocks_data || []);
      addToHistory(data.blocks_data || []);
    } catch {
    }
    setLoading(false);
  };

  const loadAvailableBlocks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cms/blocks`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setAvailableBlocks(data.blocks || []);
    } catch {
    }
  };

  const addToHistory = (newBlocks) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newBlocks)));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setPageBlocks(history[historyIndex - 1]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setPageBlocks(history[historyIndex + 1]);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = pageBlocks.findIndex(b => b.id === active.id);
      const newIndex = pageBlocks.findIndex(b => b.id === over.id);
      
      const newBlocks = arrayMove(pageBlocks, oldIndex, newIndex);
      setPageBlocks(newBlocks);
      addToHistory(newBlocks);
    }
  };

  const addBlock = (blockId) => {
    const block = availableBlocks.find(b => b.id === blockId);
    if (block) {
      const newBlocks = [...pageBlocks, block];
      setPageBlocks(newBlocks);
      addToHistory(newBlocks);
    }
  };

  const removeBlock = (blockId) => {
    const newBlocks = pageBlocks.filter(b => b.id !== blockId);
    setPageBlocks(newBlocks);
    addToHistory(newBlocks);
  };

  const updateBlock = (blockId, updatedContent) => {
    const newBlocks = pageBlocks.map(b => 
      b.id === blockId ? { ...b, content: updatedContent } : b
    );
    setPageBlocks(newBlocks);
    addToHistory(newBlocks);
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      const response = await fetch(`${API_URL}/api/cms/pages/${pageId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          blocks: pageBlocks.map(b => b.id)
        })
      });

      if (response.ok) {
        setSaveStatus('saved');
        setLastSaved(new Date());
        // Toast notification instead of alert
      } else {
        setSaveStatus('unsaved');
        alert('❌ Erreur lors de la sauvegarde');
      }
    } catch {
      setSaveStatus('unsaved');
      alert('❌ Erreur lors de la sauvegarde');
    }
  };

  const _savePage = handleSave; // Alias pour compatibilité

  const getDeviceWidth = () => {
    switch (devicePreview) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      default: return '100%';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-500">Chargement...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Toolbar */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4 mr-2" />
              Fermer
            </Button>
          )}
          <h2 className="text-lg font-semibold">{page?.name}</h2>
          <div className="flex items-center gap-2 border-l pl-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={undo}
              disabled={historyIndex <= 0}
            >
              <Undo className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
            >
              <Redo className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Help Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowHelp(!showHelp)}
            title="Raccourcis clavier"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>

          {/* Device Preview Selector */}
          <div className="flex items-center gap-1 border rounded-lg p-1">
            <Button
              variant={devicePreview === 'desktop' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDevicePreview('desktop')}
              title="Vue Desktop"
            >
              <Monitor className="h-4 w-4" />
            </Button>
            <Button
              variant={devicePreview === 'tablet' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDevicePreview('tablet')}
            >
              <Tablet className="h-4 w-4" />
            </Button>
            <Button
              variant={devicePreview === 'mobile' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDevicePreview('mobile')}
            >
              <Smartphone className="h-4 w-4" />
            </Button>
          </div>

          <Button
            variant={previewMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPreviewMode(!previewMode)}
            title="Prévisualiser (Ctrl+P)"
          >
            <Eye className="h-4 w-4 mr-2" />
            {previewMode ? 'Mode Édition' : 'Prévisualiser'}
          </Button>

          {/* Save Status Indicator */}
          <div className="flex items-center gap-2">
            {saveStatus === 'saving' && (
              <span className="text-sm text-gray-500 animate-pulse">💾 Sauvegarde...</span>
            )}
            {saveStatus === 'saved' && lastSaved && (
              <span className="text-sm text-green-600">
                ✓ Sauvegardé {new Date(lastSaved).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {saveStatus === 'unsaved' && (
              <span className="text-sm text-orange-600">⚠ Modifications non sauvegardées</span>
            )}
            
            <Button onClick={handleSave} disabled={saveStatus === 'saving'} title="Sauvegarder (Ctrl+S)">
              <Save className="h-4 w-4 mr-2" />
              Enregistrer
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Block Library */}
        {!previewMode && (
          <div className="w-80 bg-white border-r overflow-y-auto">
            <div className="p-4">
              <h3 className="font-semibold mb-4">📦 Bibliothèque de Blocs</h3>
              <div className="space-y-2">
                {availableBlocks.map(block => (
                  <div
                    key={block.id}
                    className="p-3 border rounded-lg hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => addBlock(block.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{block.name}</p>
                        <p className="text-xs text-gray-500">{block.type}</p>
                      </div>
                      <Plus className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Editor/Preview Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div 
            className="mx-auto bg-white rounded-lg shadow-lg transition-all duration-300"
            style={{ width: getDeviceWidth(), minHeight: '800px' }}
          >
            {previewMode ? (
              <PagePreview blocks={pageBlocks} />
            ) : (
              <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext 
                  items={pageBlocks.map(b => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="p-8 space-y-4">
                    {pageBlocks.length === 0 ? (
                      <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                        <p className="text-gray-500 mb-2">Aucun bloc ajouté</p>
                        <p className="text-sm text-gray-400">Glissez un bloc depuis la bibliothèque</p>
                      </div>
                    ) : (
                      pageBlocks.map((block) => (
                        <SortableBlock
                          key={block.id}
                          block={block}
                          onEdit={() => setEditingBlock(block)}
                          onDelete={() => removeBlock(block.id)}
                        />
                      ))
                    )}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">⌨️ Raccourcis Clavier</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowHelp(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="font-medium">Sauvegarder</span>
                  <kbd className="px-2 py-1 bg-white border rounded text-sm">Ctrl + S</kbd>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="font-medium">Annuler</span>
                  <kbd className="px-2 py-1 bg-white border rounded text-sm">Ctrl + Z</kbd>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="font-medium">Rétablir</span>
                  <kbd className="px-2 py-1 bg-white border rounded text-sm">Ctrl + Shift + Z</kbd>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
                  <span className="font-medium">Prévisualiser</span>
                  <kbd className="px-2 py-1 bg-white border rounded text-sm">Ctrl + P</kbd>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">💡 Astuces</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Glissez-déposez les blocs pour les réorganiser</li>
                  <li>• Utilisez les icônes crayon pour éditer le contenu</li>
                  <li>• Changez l'aperçu (Desktop/Tablet/Mobile) pour tester le responsive</li>
                  <li>• L'historique des modifications est sauvegardé automatiquement</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block Editor Modal */}
      {editingBlock && (
        <BlockEditorModal
          block={editingBlock}
          onClose={() => setEditingBlock(null)}
          onSave={(updatedContent) => {
            updateBlock(editingBlock.id, updatedContent);
            setEditingBlock(null);
          }}
        />
      )}
    </div>
  );
};

export default VisualPageEditor;